/* eslint-disable @typescript-eslint/no-inferrable-types */
import { EServiceChangeRequestStatus } from './../../../../core/models/service-change-request-status.enum';
import { EServiceChangeRequestSection } from './../../../../core/models/service-change-request-sections.enum';
import {
  Component,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  Input,
  OnInit,
  ViewEncapsulation,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  IFilterAndPaginationFieldsBase,
  IUiAlertContent,
  TUiAlertTypes,
  ToastService,
  ExtractHttpErrorResponseCodeAndMessage,
  ExtractHttpErrorResponseMessage,
  FilterFieldsToQueryParamsBuilder,
  IHttpErrorResponseCodeAndMessage,
  IHttpSingleDataResponse,
  updateApiFilter,
} from '@irembo-andela/irembogov3-common';
import { IServiceCategory } from '../../../../core/models/service-category.model';
import { ServiceChangeRequestService } from '../../../../core/services/service-change-request.service';
import { BehaviorSubject, finalize, of, switchMap } from 'rxjs';
import { IConfigStepChangeRequestMetaData } from '../../../../core/models/service-change-request-config-steps-forms.model';
import { IServiceChangeRequest } from '../../../../core/models/service-change-request.model';
import { HttpErrorResponse } from '@angular/common/http';

interface IFilterFields extends IFilterAndPaginationFieldsBase {
  name: string | null;
  keywords: string | null;
}

@Component({
  selector: 'irembogov-configuration-metadata-overview-form',
  templateUrl: './configuration-metadata-overview-form.component.html',
  styleUrls: ['configuration-metadata-overview-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ConfigurationMetadataOverviewFormComponent
  implements OnInit, OnChanges
{
  @Input() changeRequest!: IServiceChangeRequest;
  @Input() inReviewMode = false;
  @Output() closeForm: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() metadataFormSubmitted: EventEmitter<boolean> =
    new EventEmitter<boolean>();

  @ViewChild('errorMessageElement') errorMessageElement!: ElementRef;
  EServiceChangeRequestSection = EServiceChangeRequestSection;
  EServiceChangeRequestStatus = EServiceChangeRequestStatus;

  isLoading = false;

  _filterObject: BehaviorSubject<IFilterFields> =
    new BehaviorSubject<IFilterFields>({
      page: 0,
      size: 8,
      name: '',
      keywords: '',
    });

  showConfigFormErrorMessage = false;

  serviceCategories: IServiceCategory[] = [];
  serviceCategoriesBuffer: IServiceCategory[] = [];
  isLoadingServiceCategories = false;
  changeRequestStatus!: string;

  formAlertContent: IUiAlertContent = {
    title: '',
    message: '',
    type: 'warning',
  };

  requestOverviewForm: FormGroup = new FormGroup({
    serviceName: new FormControl<string>('', [Validators.required]),
    serviceSummary: new FormControl<string>('', [Validators.required]),
    categoryId: new FormControl<string>('', [Validators.required]),
    individualEnabled: new FormControl<boolean>(false),
    individualApplicantTypes: new FormControl<string[]>([]),
    businessEnabled: new FormControl<boolean>(false),
    initialRfaEnforced: new FormControl<boolean>(false),
    knowledgeBaseUrl: new FormControl<string>('', [this.urlValidator()]),
  });

  quillConfig = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['link'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ['clean'],
    ],
  };

  richTextEditorBgColor = '#ffffff';
  showHtmlCode = false;

  constructor(
    private serviceChangeRequestService: ServiceChangeRequestService,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    this.requestOverviewForm.addValidators(
      this.atLeastOneApplicantTypeEnabled()
    );
    this.requestOverviewForm.updateValueAndValidity();

    this._filterObject
      .asObservable()
      .pipe(
        switchMap((filter: IFilterFields) => {
          FilterFieldsToQueryParamsBuilder<IFilterFields>(filter);
          this.getServiceCategoriesByPage();
          return of();
        })
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['changeRequest']?.currentValue) {
      this.populateForm(this.changeRequest);
      this.changeRequestStatus = this.changeRequest.status;
    }

    if (changes['inReviewMode']?.currentValue) {
      this.inReviewMode = changes['inReviewMode'].currentValue;
      this.disableFormForReview();
    }
  }

  disableFormForReview() {
    if (this.inReviewMode) {
      this.requestOverviewForm.disable();
    }
  }

  onSearchServiceCategory(query: { term: string }) {
    updateApiFilter<IFilterFields>(this._filterObject, {
      page: 0,
      name: query?.term,
    });
  }

  getServiceCategoriesByPage() {
    this.isLoadingServiceCategories = true;
    this.serviceChangeRequestService
      .getAllServiceCategories()
      .pipe(finalize(() => (this.isLoadingServiceCategories = false)))
      .subscribe({
        next: response => {
          this.serviceCategoriesBuffer = response?.data;
        },
        error: err => {
          const errorMessage = ExtractHttpErrorResponseMessage(
            err,
            'Error getting services'
          );
          this.toastService.show({ body: errorMessage, type: 'error' });
        },
      });
  }

  onIndividualToggleChange(): void {
    const individualEnabled =
      this.requestOverviewForm.get('individualEnabled')?.value;
    if (individualEnabled) {
      const currentTypes = this.requestOverviewForm.get(
        'individualApplicantTypes'
      )?.value as string[];
      if (!currentTypes.includes('INDIVIDUAL_VERIFIED')) {
        this.requestOverviewForm
          .get('individualApplicantTypes')
          ?.setValue(['INDIVIDUAL_VERIFIED', ...currentTypes]);
      }
    }
  }

  onIndividualApplicantTypeChange(event: any): void {
    const control = this.requestOverviewForm.get(
      'individualApplicantTypes'
    ) as FormControl;
    const value = event.target.value;
    let selectedValues = [...(control.value as string[])];

    if (event.target.checked) {
      selectedValues.push(value);
    } else {
      selectedValues = selectedValues.filter(v => v !== value);
    }

    // Always ensure INDIVIDUAL_VERIFIED is present
    if (!selectedValues.includes('INDIVIDUAL_VERIFIED')) {
      selectedValues.unshift('INDIVIDUAL_VERIFIED');
    }

    control.setValue(selectedValues);
  }

  private populateForm(changeRequest: IServiceChangeRequest) {
    this.requestOverviewForm.controls['serviceName'].setValue(
      this.changeRequest.serviceName
    );
    this.requestOverviewForm.controls['serviceSummary'].setValue(
      this.changeRequest.serviceSummary
    );
    this.requestOverviewForm.controls['categoryId'].setValue(
      this.changeRequest.categoryId
    );

    // Set knowledgeBaseUrl if it exists
    if (this.changeRequest.knowledgeBaseUrl) {
      this.requestOverviewForm.controls['knowledgeBaseUrl'].setValue(
        this.changeRequest.knowledgeBaseUrl
      );
    }
    this.requestOverviewForm.controls['initialRfaEnforced'].setValue(
      this.changeRequest.initialRfaEnforced ?? false
    );

    const individualEnabled =
      changeRequest.applicantType === 'BOTH' ||
      changeRequest.applicantType === 'INDIVIDUAL';
    const businessEnabled =
      changeRequest.applicantType === 'BOTH' ||
      changeRequest.applicantType === 'BUSINESS';

    this.requestOverviewForm.controls['individualEnabled'].setValue(
      individualEnabled
    );
    this.requestOverviewForm.controls['businessEnabled'].setValue(
      businessEnabled
    );

    const individualApplicantTypes = changeRequest.individualApplicantTypes
      ?.length
      ? changeRequest.individualApplicantTypes
      : individualEnabled
      ? ['INDIVIDUAL_VERIFIED']
      : [];
    this.requestOverviewForm.controls['individualApplicantTypes'].setValue(
      individualApplicantTypes
    );

    this.requestOverviewForm.markAllAsTouched();
    this.requestOverviewForm.updateValueAndValidity();
  }

  private atLeastOneApplicantTypeEnabled(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const group = control as FormGroup;
      const individualEnabled = group.get('individualEnabled')?.value;
      const businessEnabled = group.get('businessEnabled')?.value;
      return individualEnabled || businessEnabled
        ? null
        : { noApplicantType: true };
    };
  }

  private urlValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        // If the field is empty, it's valid since it's optional
        return null;
      }

      try {
        const url = new URL(control.value);
        // Check if protocol is http or https
        if (url.protocol !== 'http:' && url.protocol !== 'https:') {
          return { invalidProtocol: true };
        }
        return null;
      } catch (error) {
        // If URL constructor throws an error, the URL is invalid
        return { invalidUrl: true };
      }
    };
  }

  onFormSubmit() {
    if (!this.changeRequest.id) return;
    if (!this.checkIfConfigFormValid(this.requestOverviewForm)) return;

    this.updateFormDisplayedErrorMessageContent(false);
    this.isLoading = true;

    const individualEnabled: boolean =
      this.requestOverviewForm.controls['individualEnabled'].value;
    const businessEnabled: boolean =
      this.requestOverviewForm.controls['businessEnabled'].value;

    let applicantType: 'INDIVIDUAL' | 'BUSINESS' | 'BOTH' | '' = '';
    if (individualEnabled && businessEnabled) {
      applicantType = 'BOTH';
    } else if (individualEnabled) {
      applicantType = 'INDIVIDUAL';
    } else if (businessEnabled) {
      applicantType = 'BUSINESS';
    }

    const individualApplicantTypes: string[] = individualEnabled
      ? this.requestOverviewForm.controls['individualApplicantTypes'].value
      : [];

    const metadata: IConfigStepChangeRequestMetaData = {
      serviceName: this.requestOverviewForm.controls['serviceName'].value,
      serviceSummary: this.requestOverviewForm.controls['serviceSummary'].value,
      categoryId: this.requestOverviewForm.controls['categoryId'].value,
      trafficClass: 'regular',
      applicantType: applicantType,
      individualApplicantTypes: individualApplicantTypes,
      knowledgeBaseUrl:
        this.requestOverviewForm.controls['knowledgeBaseUrl'].value,
      initialRfaEnforced:
        this.requestOverviewForm.controls['initialRfaEnforced'].value,
    };

    this.serviceChangeRequestService
      .updateServiceChangeRequestMetadata(this.changeRequest.id, metadata)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (res: IHttpSingleDataResponse<IServiceChangeRequest>) => {
          this.toastService.show({
            body: res.responseMessage,
            type: 'success',
          });
          this.metadataFormSubmitted.emit(this.requestOverviewForm.valid);
        },
        error: (error: HttpErrorResponse) => {
          const errorMessageAndCode: IHttpErrorResponseCodeAndMessage =
            ExtractHttpErrorResponseCodeAndMessage(
              error,
              `Failed to update Change request metadata`
            );
          this.toastService.show({
            body: errorMessageAndCode.message,
            type: 'error',
          });

          this.updateFormDisplayedErrorMessageContent(
            true,
            'danger',
            'Metadata Not Saved',
            errorMessageAndCode.message
          );

          this.metadataFormSubmitted.emit(false);
        },
      });
  }

  updateFormDisplayedErrorMessageContent(
    show: boolean,
    type: TUiAlertTypes = 'warning',
    title: string = '',
    message: string = ''
  ) {
    this.showConfigFormErrorMessage = show;
    this.formAlertContent = { type, title, message };
    if (show) {
      this.errorMessageElement.nativeElement.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }

  private checkIfConfigFormValid(configForm: FormGroup): boolean {
    if (configForm.invalid) {
      this.updateFormDisplayedErrorMessageContent(
        true,
        'danger',
        'Invalid fields!',
        'Please ensure you have filled in all the required fields.'
      );
      return false;
    }
    return true;
  }

  wordCount(value: string | undefined) {
    if (!value && typeof value !== 'string') return 0;
    const textOnly = value.replace(/<[^>]*>/g, '').trim();
    const wordCount = textOnly === '' ? 0 : textOnly.split(/\s+/).length;
    return wordCount;
  }

  hideShowHtml() {
    this.showHtmlCode = !this.showHtmlCode;
  }
}
